import { engravePhrase } from './engrave.ts';
import { expectedTapTicks, phraseMeter, tupletAt, type RhythmPhrase } from './phrase.ts';

/** What a phrase actually contains, as a student reads and plays it. */
export interface PhraseFeatures {
  /** Notes starting part-way through a beat (the "and"; the 2nd/3rd eighth in compound meters). */
  divisions: boolean;
  /** Notes on the finer subdivision (sixteenths). */
  subdivisions: boolean;
  /** A dotted rhythm. In compound meters the dotted beat itself doesn't count. */
  dotted: boolean;
  rests: boolean;
  ties: boolean;
  /** A triplet group (three in the time of two). */
  triplets: boolean;
}

/** Shared by generation (to guarantee requested features) and skill tagging, so both agree. */
export function phraseFeatures(phrase: RhythmPhrase): PhraseFeatures {
  const ts = phraseMeter(phrase);
  const division = ts.kind === 'compound' ? ts.beatTicks / 3 : ts.beatTicks / 2;
  // Notes inside triplets are counted as triplets, not as divisions of the beat.
  const taps = expectedTapTicks(phrase).filter((t) => !tupletAt(phrase, t));
  const items = engravePhrase(phrase).flatMap((m) => m.items);
  return {
    divisions: taps.some((t) => t % ts.beatTicks !== 0 && t % division === 0),
    subdivisions: taps.some((t) => t % division !== 0),
    dotted: items.some((i) => !i.rest && i.dots > 0 && !(ts.kind === 'compound' && i.duration % ts.beatTicks === 0)),
    rests: phrase.events.some((e) => e.rest),
    ties: items.some((i) => i.tieToNext),
    triplets: (phrase.tuplets?.length ?? 0) > 0,
  };
}
